import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { uploadRelatedReportApi } from '../../../../rtk-apis/relatedReports/uploadRelatedReport';
import { deleteRelatedReportApi } from '../../../../rtk-apis/relatedReports/deleteRelatedReport';
import { getRelatedReportApi } from '../../../../rtk-apis/relatedReports/getRelatedReport';
import { clearState as clearUploadRelatedReportState } from '../../../../store/slices/relatedReports/uploadRelatedReport';
import { clearState as clearDeleteRelatedReportState } from '../../../../store/slices/relatedReports/deleteRelatedReport';
import { clearState as clearGetRelatedReportState } from '../../../../store/slices/relatedReports/getRelatedReport';
import { AppDispatch, RootState } from '../../../../store/store';
import { getHospitalId } from '../../../../Utils/authUtils';
import { utils } from '@ohif/core';
import Success from '../../../../components/Success';
import Failed from '../../../../components/Failed';
import ConfirmationModal from '../../../../components/ConfirmationModal';

const { formatDate } = utils;

type Props = {
  onClose?: () => void;
  isAttachmentsModalOpen: boolean;
  studyInstanceUID: string;
};

const MAX_SIZE = 50 * 1024 * 1024; // 50 MB
// Accepted upload types. XML is the CardioSoft stress export we auto-read; the
// others are the existing scanned-document / image attachments.
const ACCEPT = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
  'application/xml': ['.xml'],
  'text/xml': ['.xml'],
};
const SUPPORTED_LABEL = 'JPEG, PNG, PDF, XML';

// palette (matches the app's navy/teal surface)
const C = {
  surface: '#0e1c2e',
  surface2: '#16263b',
  line: '#243652',
  ink: '#e9f0f8',
  ink2: '#9fb2cc',
  teal: '#2dd4bf',
  red: '#fb7185',
};

const ext = (name = '') => name.toLowerCase().split('.').pop() || '';
const isPdf = (name = '') => ext(name) === 'pdf';
const isXml = (name = '') => ext(name) === 'xml';
const isImage = (name = '') => ['jpg', 'jpeg', 'png'].includes(ext(name));

function fileIcon(name: string) {
  if (isPdf(name)) {
    return <PictureAsPdfOutlinedIcon sx={{ color: C.red }} />;
  }
  if (isXml(name)) {
    return <CodeOutlinedIcon sx={{ color: C.teal }} />;
  }
  if (isImage(name)) {
    return <ImageOutlinedIcon sx={{ color: C.ink2 }} />;
  }
  return <InsertDriveFileOutlinedIcon sx={{ color: C.ink2 }} />;
}

const PatientAttachmentsModal = ({ onClose, isAttachmentsModalOpen, studyInstanceUID }: Props) => {
  useParams();
  const dispatch = useDispatch<AppDispatch>();

  const {
    status: uploadStatus,
    loading: uploadLoading,
    msg: uploadMsg,
  } = useSelector((state: RootState) => state.uploadRelatedReportResponse);
  const {
    status: getStatus,
    result: getResult,
    msg: getMsg,
  } = useSelector((state: RootState) => state.getRelatedReportResponse);
  const { status: deleteStatus, msg: deleteMsg } = useSelector(
    (state: RootState) => state.deleteRelatedReportResponse
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [displayMessage, setDisplayMessage] = useState('');
  const [loader, setLoader] = useState(false);

  const onDrop = useCallback((accepted: File[], rejections: any[]) => {
    setDropError(null);
    if (rejections?.length) {
      const code = rejections[0]?.errors?.[0]?.code;
      setSelectedFile(null);
      setDropError(
        code === 'file-too-large'
          ? 'File is too large. Maximum size is 50 MB.'
          : code === 'file-invalid-type'
            ? `Unsupported file type. Allowed: ${SUPPORTED_LABEL}.`
            : 'That file could not be added.'
      );
      return;
    }
    if (accepted?.[0]) {
      setSelectedFile(accepted[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxFiles: 1,
    maxSize: MAX_SIZE,
  });

  const fetchList = () =>
    dispatch(
      getRelatedReportApi({ studyInstanceUID, hospitalId: getHospitalId(), limit: 10, page: 1 })
    );

  const handleUpload = () => {
    if (!selectedFile) {
      return;
    }
    setLoader(true);
    const formData = new FormData();
    formData.append(
      'hospitalId',
      sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId') || ''
    );
    formData.append('file', selectedFile);
    formData.append('studyInstanceUID', studyInstanceUID);
    dispatch(uploadRelatedReportApi(formData));
  };

  const handleDelete = () => {
    setLoader(true);
    setIsConfirmationModalOpen(false);
    dispatch(deleteRelatedReportApi({ prescriptionId: deleteId }));
  };

  const openAttachment = (item: any) => {
    if (item?.documentData) {
      window.open(item.documentData, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    if (isAttachmentsModalOpen) {
      setLoader(true);
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAttachmentsModalOpen]);

  useEffect(() => {
    const onSuccess = (message: string) => {
      setLoader(false);
      setDisplayMessage(message);
      setIsSuccessModalOpen(true);
      setSelectedFile(null);
      setDropError(null);
      fetchList();
    };
    const onFailure = (message: string) => {
      setLoader(false);
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };

    if (!isAttachmentsModalOpen) {
      return;
    }
    if (getStatus === 'SUCCESS') {
      setLoader(false);
      setAttachments(getResult || []);
    }
    if (getStatus === 'FAILED') {
      onFailure(getMsg);
    }
    if (uploadStatus === 'SUCCESS') {
      onSuccess(uploadMsg);
    }
    if (uploadStatus === 'FAILED') {
      onFailure(uploadMsg);
    }
    if (deleteStatus === 'SUCCESS') {
      onSuccess(deleteMsg);
    }
    if (deleteStatus === 'FAILED') {
      onFailure(deleteMsg);
    }

    return () => {
      dispatch(clearGetRelatedReportState());
      dispatch(clearDeleteRelatedReportState());
      dispatch(clearUploadRelatedReportState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getStatus, uploadStatus, deleteStatus]);

  const busy = loader || uploadLoading;

  return (
    <>
      <Dialog
        open={isAttachmentsModalOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              bgcolor: C.surface,
              color: C.ink,
              border: `1px solid ${C.line}`,
              borderRadius: 3,
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}
        >
          <Typography
            component="span"
            sx={{ fontSize: 18, fontWeight: 700, color: C.ink }}
          >
            Patient Attachments
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: C.ink2 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ borderColor: C.line }}
        >
          {/* Existing attachments */}
          {attachments.length > 0 && (
            <List
              disablePadding
              sx={{ mb: 2 }}
            >
              {attachments.map((item, index) => (
                <ListItem
                  key={item._id || index}
                  disableGutters
                  sx={{
                    bgcolor: C.surface2,
                    border: `1px solid ${C.line}`,
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    mb: 1,
                    gap: 1.5,
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Open">
                        <IconButton
                          size="small"
                          sx={{ color: C.ink2 }}
                          onClick={() => openAttachment(item)}
                        >
                          <OpenInNewOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          sx={{ color: C.red }}
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsConfirmationModalOpen(true);
                          }}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  {fileIcon(item.documentName)}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: C.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={item.documentName}
                    >
                      {item.documentName}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: C.ink2 }}>
                      {formatDate(item.createdAt)}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}

          {/* Dropzone */}
          <Box
            {...getRootProps()}
            sx={{
              border: `2px dashed ${isDragActive ? C.teal : C.line}`,
              borderRadius: 2,
              bgcolor: isDragActive ? 'rgba(45,212,191,0.06)' : C.surface2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color .15s, background-color .15s',
            }}
          >
            <input {...getInputProps()} />
            {selectedFile ? (
              <Chip
                icon={fileIcon(selectedFile.name)}
                label={`${selectedFile.name} · ${(selectedFile.size / 1024).toFixed(0)} KB`}
                onDelete={e => {
                  // stop the dropzone click from firing the file picker
                  (e as any).stopPropagation?.();
                  setSelectedFile(null);
                }}
                sx={{
                  bgcolor: C.surface,
                  color: C.ink,
                  border: `1px solid ${C.line}`,
                  maxWidth: '100%',
                }}
              />
            ) : (
              <>
                <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: C.teal, mb: 1 }} />
                <Typography sx={{ fontSize: 14, color: C.ink }}>
                  Drag &amp; drop a file or{' '}
                  <Box
                    component="span"
                    sx={{ color: C.teal, textDecoration: 'underline' }}
                  >
                    browse
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: C.ink2, mt: 0.5 }}>
                  Supported: {SUPPORTED_LABEL} · max 50 MB
                </Typography>
              </>
            )}
          </Box>
          {dropError && (
            <Typography sx={{ color: C.red, fontSize: 12.5, mt: 1 }}>{dropError}</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ color: C.ink2, borderColor: C.line, '&:hover': { borderColor: C.ink2 } }}
          >
            Close
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || busy}
            startIcon={
              busy ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                />
              ) : null
            }
            sx={{
              bgcolor: C.teal,
              color: '#04121a',
              fontWeight: 700,
              '&:hover': { bgcolor: '#25b6a4' },
              '&.Mui-disabled': { bgcolor: C.line, color: C.ink2 },
            }}
          >
            {busy ? 'Uploading…' : 'Upload Attachment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Success
        isSuccessModalOpen={isSuccessModalOpen}
        title={displayMessage}
        handleCloseModal={() => setIsSuccessModalOpen(false)}
      />
      <Failed
        isFailedModalOpen={isFailedModalOpen}
        title={displayMessage || 'Operation failed'}
        handleCloseModal={() => setIsFailedModalOpen(false)}
      />
      <ConfirmationModal
        title="Delete Attachment"
        description="Are you sure you want to delete this attachment?"
        isConfirmationModalOpen={isConfirmationModalOpen}
        handleCloseModal={() => setIsConfirmationModalOpen(false)}
        handleConfirm={handleDelete}
        cancelActionText="Cancel"
        confirmActionText="Delete"
      />
    </>
  );
};

export default PatientAttachmentsModal;
